import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Box, Divider, IconButton, TextField, Tooltip } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { CachePolicies } from "use-http";

import { getConfig } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useValueDebounce } from "hooks/useValueDebounce";
import { Typography } from "new-components/typography/Typography";
import { Detector } from "types/detectors";

const config = getConfig();

const { JANZU_URL } = config;

export interface FirstAnswerTimeProps {
  firstAnswerTimeValue: number;
  detectorId: string;
  refetchData: (route?: string | undefined) => Promise<Detector>;
}

export const FirstAnswerTime = React.memo((props: FirstAnswerTimeProps) => {
  const { detectorId, firstAnswerTimeValue, refetchData } = props;

  const [disable, setDisable] = useState<boolean>(true);

  const [timeValue, setTimeValue] = useState<number>(0);
  const debouncedValue = useValueDebounce<number>(timeValue, 1000);

  const url = `${JANZU_URL}reef-api/detectors/${detectorId}/patience`;
  const { patch: updateDetectorDetail } = useFetchCustom<string>(url, { cachePolicy: CachePolicies.NO_CACHE });

  const updatePatienceTime = useCallback(async () => {
    await updateDetectorDetail({ patience_time: debouncedValue });
    await refetchData();
    enqueueSnackbar("First answer time updated successfully ", {
      variant: "success",
    });
  }, [debouncedValue, refetchData, updateDetectorDetail]);

  useEffect(() => {
    if (debouncedValue && debouncedValue >= 10 && debouncedValue >= 0) {
      updatePatienceTime();
    } else if (debouncedValue && debouncedValue < 10) {
      enqueueSnackbar("First answer time must be 10 or higher.", {
        variant: "error",
      });
    }
  }, [debouncedValue, updatePatienceTime]);

  const handleClickDisable = () => setDisable(!disable);

  return (
    <Box>
      <Box className="flex justify-between items-center mb-2">
        <Box>
          <Typography variant="detail" textTransform="uppercase" className="text-neutral-700">
            <b>first answer time:</b>
          </Typography>
        </Box>

        <Box>
          <Tooltip
            title="First answer time is how long your application can wait for a confident answer, including from a human cloud labeler. The image query's 'first answer' is defined as the best answer Groundlight has determined within the specified first answer time."
            placement="right"
            arrow
          >
            <IconButton className="p-0">
              <InfoIcon className="text-black" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box>
        <Box className="flex justify-between items-center gap-2">
          <TextField
            disabled={disable}
            defaultValue={firstAnswerTimeValue}
            onChange={(e) => setTimeValue(Number(e.target.value))}
            size="small"
            fullWidth
            type="number"
            id="first-answer-time-text-field"
          />
          <Typography>
            Seconds
          </Typography>
          <IconButton onClick={handleClickDisable}>
            {disable ? <LockIcon /> : <LockOpenIcon />}
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ marginTop: "10px", marginBottom: "20px" }} />
    </Box>
  );
});
